/**
 * DedicatedOfferDialog — Multi-step dialog to CREATE or EDIT a dedicated customer offer.
 *
 * Used by both Admin (`/admin` → tab "dedicated-offers") and Agent (`/agent/dedicated-offers`).
 *
 * Steps:
 *  1. Pick customer (autocomplete by name/codice/P.IVA)
 *  2. Pick pricelist (default = customer.pricelist_type_id, admin sees all, agent only assigned)
 *  3. Search + add products of that pricelist
 *  4. For each product: see base_price (read-only) + edit discount % (0..99) → see final_price
 *  5. Save -> POST/PUT API + optional immediate PDF preview
 */
import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Search, Plus, Trash2, FileText, Save, Loader2, Sparkles, ChevronUp, ChevronDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ScrollArea } from "./ui/scroll-area";
import { Separator } from "./ui/separator";
import { Textarea } from "./ui/textarea";
import { openAuthenticatedPdf } from "../utils/pdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const round05 = (v) => {
  const n = Number(v) || 0;
  return Math.round(n * 20) / 20;
};

export default function DedicatedOfferDialog({ open, onClose, onSaved, editOffer = null }) {
  const isEdit = !!editOffer;

  // Step state
  const [step, setStep] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [pricelists, setPricelists] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [pricelistId, setPricelistId] = useState("");
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [productsLoading, setProductsLoading] = useState(false);
  const [items, setItems] = useState([]); // [{product_id, is_free, code, description, unit, vat_rate, base_price, discount_pct}]
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  // === Prospect mode (cliente potenziale non in anagrafica) ===
  const emptyProspect = { business_name: "", vat_number: "", address: "", city: "", email: "", phone: "" };
  const [isProspect, setIsProspect] = useState(false);
  const [prospectData, setProspectData] = useState(emptyProspect);
  const [language, setLanguage] = useState("it"); // "it" | "en" — usato per il PDF
  // Tracks whether the pricelist was changed by the user after opening (in edit mode we keep
  // saved base_price unless admin explicitly switches pricelist).
  const initialPricelistRef = useRef("");

  // Reset on open / load on open
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [cu, pl] = await Promise.all([
          axios.get(`${API}/customers`),
          axios.get(`${API}/pricelist-types`),
        ]);
        setCustomers(cu.data || []);
        setPricelists(pl.data || []);
      } catch (e) {
        toast.error("Errore nel caricamento dati");
        console.debug(e);
      }
    })();
    if (isEdit && editOffer) {
      const ep = !!editOffer.is_prospect;
      setIsProspect(ep);
      setProspectData(ep && editOffer.prospect_data
        ? { ...emptyProspect, ...editOffer.prospect_data }
        : emptyProspect);
      setLanguage((editOffer.language || "it").toLowerCase() === "en" ? "en" : "it");
      setCustomerId(editOffer.customer_id || "");
      setPricelistId(editOffer.pricelist_type_id);
      initialPricelistRef.current = editOffer.pricelist_type_id;
      setItems(
        (editOffer.items || []).map((it) => ({
          product_id: it.product_id,
          is_free: !!it.is_free || !it.product_id,
          code: it.code,
          description: it.description,
          description_en: it.description_en || "",
          unit: it.unit || "Kg",
          vat_rate: it.vat_rate,
          base_price: it.base_price,
          discount_pct: it.discount_pct || 0,
          category_name: it.category_name || "",
        }))
      );
      setNotes(editOffer.notes || "");
      setStep(3); // go directly to items
    } else {
      setIsProspect(false);
      setProspectData(emptyProspect);
      setLanguage("it");
      setCustomerId("");
      setPricelistId("");
      initialPricelistRef.current = "";
      setItems([]);
      setNotes("");
      setCustomerSearch("");
      setProductSearch("");
      setStep(1);
    }
  }, [open, isEdit, editOffer]);

  // Auto-fill pricelist when customer is picked
  useEffect(() => {
    if (!customerId || isEdit) return;
    const cust = customers.find((c) => c.id === customerId);
    if (cust?.pricelist_type_id && pricelists.some((p) => p.id === cust.pricelist_type_id)) {
      setPricelistId(cust.pricelist_type_id);
    }
  }, [customerId, customers, pricelists, isEdit]);

  // Load products when pricelist changes — uses the dedicated preview endpoint that
  // honors the SPECIFIC pricelist chosen in step 2 (NOT the customer default).
  // For prospect mode customer_id is omitted (listino defaults are used).
  useEffect(() => {
    if (!pricelistId) return;
    if (!isProspect && !customerId) return;
    setProductsLoading(true);
    (async () => {
      try {
        const params = { pricelist_type_id: pricelistId };
        if (!isProspect && customerId) params.customer_id = customerId;
        const r = await axios.get(`${API}/dedicated-offers/preview-catalog`, { params });
        const list = (r.data?.products || []).map((p) => ({
          id: p.id, code: p.code, description: p.description,
          description_en: p.description_en || "",
          unit: p.unit || "Kg", vat_rate: p.vat_rate ?? 0.10,
          category_name: p.category_name || "",
          price: p.price ?? 0,
        }));
        setProducts(list);
        // Re-align base_price for non-free items already added. In edit mode skip this UNTIL
        // the user explicitly switches pricelist (otherwise the saved base_price would be silently
        // overwritten with today's listino value on every dialog open).
        const pricelistChanged = !isEdit || (pricelistId !== initialPricelistRef.current);
        if (pricelistChanged) {
          setItems((prev) => prev.map((it) => {
            if (it.is_free) return it;
            const fresh = list.find((p) => p.id === it.product_id);
            if (!fresh) return it;
            return { ...it, base_price: Number(fresh.price) || 0 };
          }));
        }
      } catch (e) {
        toast.error("Errore nel caricamento prodotti");
        console.debug(e);
      } finally {
        setProductsLoading(false);
      }
    })();
  }, [pricelistId, customerId, isProspect]);

  const filteredCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return customers.slice(0, 50);
    return customers.filter((c) =>
      (c.business_name || "").toLowerCase().includes(q) ||
      (c.codice_cliente || "").toLowerCase().includes(q) ||
      (c.vat_number || "").toLowerCase().includes(q)
    ).slice(0, 50);
  }, [customers, customerSearch]);

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    const taken = new Set(items.map((i) => i.product_id));
    const base = products.filter((p) => !taken.has(p.id));
    if (!q) return base.slice(0, 50);
    return base.filter((p) =>
      q.split(/\s+/).every((tok) =>
        (p.code || "").toLowerCase().includes(tok) ||
        (p.description || "").toLowerCase().includes(tok) ||
        (p.category_name || "").toLowerCase().includes(tok)
      )
    ).slice(0, 80);
  }, [products, productSearch, items]);

  const addProduct = (p) => {
    setItems((prev) => [
      ...prev,
      {
        product_id: p.id,
        is_free: false,
        code: p.code,
        description: p.description,
        description_en: p.description_en || "",
        unit: p.unit,
        vat_rate: p.vat_rate,
        base_price: Number(p.price) || 0,
        discount_pct: 0,
        category_name: p.category_name,
      },
    ]);
    setProductSearch("");
  };

  // ===== Free item (articolo libero) =====
  const [freeOpen, setFreeOpen] = useState(false);
  const [freeForm, setFreeForm] = useState({ code: "", description: "", unit: "Kg", base_price: "" });
  const resetFreeForm = () => setFreeForm({ code: "", description: "", unit: "Kg", base_price: "" });
  const addFreeItem = () => {
    if (!freeForm.description.trim()) { toast.error("Descrizione obbligatoria"); return; }
    const bp = Number(freeForm.base_price);
    if (!bp || bp <= 0) { toast.error("Prezzo base deve essere > 0"); return; }
    setItems((prev) => [
      ...prev,
      {
        product_id: null,
        is_free: true,
        code: freeForm.code.trim(),
        description: freeForm.description.trim(),
        unit: (freeForm.unit || "Kg").trim(),
        vat_rate: 0.10,
        base_price: bp,
        discount_pct: 0,
        category_name: "",
      },
    ]);
    resetFreeForm();
    setFreeOpen(false);
  };

  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const moveItem = (idx, dir) => {
    setItems((prev) => {
      const j = idx + dir;
      if (j < 0 || j >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return arr;
    });
  };
  const updateDiscount = (idx, val) => {
    const num = Math.max(0, Math.min(99, Number(val) || 0));
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, discount_pct: num } : it)));
  };
  const updateFreeBasePrice = (idx, val) => {
    const num = Math.max(0, Number(val) || 0);
    setItems((prev) => prev.map((it, i) => (i === idx && it.is_free ? { ...it, base_price: num } : it)));
  };

  const computedFinal = (it) => round05(it.base_price * (1 - (it.discount_pct || 0) / 100));

  // Returns the description in the currently selected language, falling back to IT.
  const displayDesc = (it) => {
    if (language === "en" && (it.description_en || "").trim()) return it.description_en;
    return it.description || "";
  };

  const canProceedStep1 = isProspect ? !!prospectData.business_name.trim() : !!customerId;
  const canProceedStep2 = !!pricelistId;

  const handleSave = async (openPdf = false) => {
    if (isProspect) {
      if (!prospectData.business_name.trim()) {
        toast.error("Inserisci il nome / ragione sociale del prospect");
        return;
      }
    } else if (!customerId) {
      toast.error("Seleziona un cliente");
      return;
    }
    if (!pricelistId) {
      toast.error("Seleziona un listino");
      return;
    }
    if (!items.length) {
      toast.error("Aggiungi almeno un articolo");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customer_id: isProspect ? null : customerId,
        pricelist_type_id: pricelistId,
        is_prospect: isProspect,
        prospect_data: isProspect ? {
          business_name: prospectData.business_name.trim(),
          vat_number: (prospectData.vat_number || "").trim(),
          address: (prospectData.address || "").trim(),
          city: (prospectData.city || "").trim(),
          email: (prospectData.email || "").trim(),
          phone: (prospectData.phone || "").trim(),
        } : null,
        language,
        items: items.map((it) => it.is_free ? {
          product_id: null,
          is_free: true,
          code: it.code,
          description: it.description,
          unit: it.unit,
          base_price: Number(it.base_price) || 0,
          vat_rate: Number(it.vat_rate) || 0.10,
          discount_pct: Number(it.discount_pct) || 0,
        } : {
          product_id: it.product_id,
          discount_pct: Number(it.discount_pct) || 0,
        }),
        notes: notes.trim(),
      };
      let saved;
      if (isEdit) {
        const r = await axios.put(`${API}/dedicated-offers/${editOffer.id}`, payload);
        saved = r.data;
      } else {
        const r = await axios.post(`${API}/dedicated-offers`, payload);
        saved = r.data;
      }
      toast.success(isEdit ? "Offerta aggiornata" : "Offerta creata");
      if (onSaved) onSaved(saved);
      if (openPdf && saved?.id) {
        openAuthenticatedPdf(`/dedicated-offers/${saved.id}/pdf`);
      }
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Errore nel salvataggio";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-4xl max-h-[92vh] flex flex-col" data-testid="dedicated-offer-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl text-stone-900">
            {isEdit ? "Modifica offerta dedicata" : "Nuova offerta dedicata"}
          </DialogTitle>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-2 px-1 -mt-2 mb-2 text-[11px] font-medium">
          {["Cliente", "Listino", "Articoli"].map((label, i) => {
            const idx = i + 1;
            const active = step === idx;
            const step1Ready = isProspect ? !!prospectData.business_name.trim() : !!customerId;
            const done =
              (idx === 1 && step1Ready) ||
              (idx === 2 && !!pricelistId) ||
              (idx === 3 && items.length > 0);
            return (
              <React.Fragment key={label}>
                <button
                  type="button"
                  onClick={() => {
                    if (idx === 1) setStep(1);
                    if (idx === 2 && step1Ready) setStep(2);
                    if (idx === 3 && step1Ready && pricelistId) setStep(3);
                  }}
                  className={`px-3 py-1 rounded-full border ${active
                    ? "bg-[#8A1C2A] text-white border-[#8A1C2A]"
                    : done
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-stone-100 text-stone-500 border-stone-200"
                    }`}
                  data-testid={`do-step-${idx}-btn`}
                >
                  {idx}. {idx === 1 && isProspect ? "Prospect" : label}
                </button>
                {idx < 3 && <span className="text-stone-300">→</span>}
              </React.Fragment>
            );
          })}
        </div>

        <ScrollArea className="flex-1 pr-2">
          {/* STEP 1 — Customer / Prospect */}
          {step === 1 && (
            <div className="space-y-3" data-testid="do-step-1-pane">
              {/* Toggle Cliente esistente / Prospect */}
              <div className="flex flex-wrap items-center gap-2 p-2 bg-stone-50 border border-stone-200 rounded-lg">
                <button
                  type="button"
                  onClick={() => { setIsProspect(false); setCustomerSearch(""); }}
                  disabled={isEdit}
                  className={`flex-1 min-w-[120px] px-3 py-2 rounded-md text-sm font-medium transition border ${
                    !isProspect
                      ? "bg-[#8A1C2A] text-white border-[#8A1C2A]"
                      : "bg-white text-stone-600 border-stone-200 hover:bg-stone-100"
                  } ${isEdit ? "opacity-60 cursor-not-allowed" : ""}`}
                  data-testid="do-mode-existing-btn"
                >
                  Cliente esistente
                </button>
                <button
                  type="button"
                  onClick={() => { setIsProspect(true); setCustomerId(""); }}
                  disabled={isEdit}
                  className={`flex-1 min-w-[120px] px-3 py-2 rounded-md text-sm font-medium transition border ${
                    isProspect
                      ? "bg-[#C5A059] text-white border-[#C5A059]"
                      : "bg-white text-stone-600 border-stone-200 hover:bg-stone-100"
                  } ${isEdit ? "opacity-60 cursor-not-allowed" : ""}`}
                  data-testid="do-mode-prospect-btn"
                >
                  <Sparkles className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                  Cliente potenziale (Prospect)
                </button>
              </div>

              {isProspect ? (
                <div className="space-y-3 border border-amber-200 bg-amber-50/30 rounded-lg p-4" data-testid="do-prospect-form">
                  <p className="text-xs text-amber-800">
                    <Sparkles className="w-3 h-3 inline mr-1" />
                    L&apos;offerta verrà generata <b>senza salvare il cliente</b> nelle anagrafiche.
                    Compila almeno la ragione sociale; gli altri campi appariranno nel PDF.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                      <Label className="text-xs font-medium">Ragione sociale / Nome *</Label>
                      <Input
                        value={prospectData.business_name}
                        onChange={(e) => setProspectData((p) => ({ ...p, business_name: e.target.value }))}
                        placeholder="Es. Trattoria Da Mario"
                        data-testid="do-prospect-business-name"
                        autoFocus
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium">P.IVA / Codice fiscale</Label>
                      <Input
                        value={prospectData.vat_number}
                        onChange={(e) => setProspectData((p) => ({ ...p, vat_number: e.target.value }))}
                        placeholder="IT01234567890"
                        data-testid="do-prospect-vat"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium">Lingua PDF</Label>
                      <Select value={language} onValueChange={setLanguage}>
                        <SelectTrigger data-testid="do-prospect-language">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="it">Italiano</SelectItem>
                          <SelectItem value="en">English</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs font-medium">Indirizzo</Label>
                      <Input
                        value={prospectData.address}
                        onChange={(e) => setProspectData((p) => ({ ...p, address: e.target.value }))}
                        placeholder="Via Roma 10"
                        data-testid="do-prospect-address"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium">Città</Label>
                      <Input
                        value={prospectData.city}
                        onChange={(e) => setProspectData((p) => ({ ...p, city: e.target.value }))}
                        placeholder="Milano (MI)"
                        data-testid="do-prospect-city"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium">Telefono</Label>
                      <Input
                        value={prospectData.phone}
                        onChange={(e) => setProspectData((p) => ({ ...p, phone: e.target.value }))}
                        placeholder="+39 333 1234567"
                        data-testid="do-prospect-phone"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs font-medium">Email</Label>
                      <Input
                        type="email"
                        value={prospectData.email}
                        onChange={(e) => setProspectData((p) => ({ ...p, email: e.target.value }))}
                        placeholder="info@example.com"
                        data-testid="do-prospect-email"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <Label className="text-sm font-medium">Cerca cliente</Label>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                    <Input
                      className="pl-10"
                      placeholder="Ragione sociale, codice o P.IVA..."
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      data-testid="do-customer-search"
                    />
                  </div>
                  <div className="divide-y divide-stone-100 border border-stone-200 rounded-lg overflow-hidden max-h-[50vh] overflow-y-auto">
                    {filteredCustomers.map((c) => {
                      const sel = c.id === customerId;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setCustomerId(c.id)}
                          className={`w-full text-left px-4 py-3 flex items-center justify-between hover:bg-stone-50 transition ${sel ? "bg-[#8A1C2A]/5" : ""}`}
                          data-testid={`do-customer-${c.id}`}
                        >
                          <div className="min-w-0">
                            <p className="font-medium text-stone-900 truncate">{c.business_name}</p>
                            <p className="text-xs text-stone-500 truncate">{c.codice_cliente} · {c.vat_number || "—"}</p>
                          </div>
                          {sel && <Badge className="bg-[#8A1C2A] text-white">Selezionato</Badge>}
                        </button>
                      );
                    })}
                    {filteredCustomers.length === 0 && (
                      <div className="px-4 py-8 text-center text-sm text-stone-500">Nessun cliente trovato</div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* STEP 2 — Pricelist */}
          {step === 2 && (
            <div className="space-y-3" data-testid="do-step-2-pane">
              <Label className="text-sm font-medium">Listino di partenza</Label>
              <p className="text-xs text-stone-500">
                Il prezzo base di ogni articolo verrà calcolato da questo listino. Potrai poi applicare uno sconto % per riga.
              </p>
              <div className="divide-y divide-stone-100 border border-stone-200 rounded-lg overflow-hidden">
                {pricelists.map((pl) => {
                  const sel = pl.id === pricelistId;
                  return (
                    <button
                      key={pl.id}
                      type="button"
                      onClick={() => setPricelistId(pl.id)}
                      className={`w-full text-left px-4 py-3 flex items-center justify-between hover:bg-stone-50 transition ${sel ? "bg-[#8A1C2A]/5" : ""}`}
                      data-testid={`do-pricelist-${pl.id}`}
                    >
                      <div>
                        <p className="font-medium text-stone-900">{pl.name}</p>
                        <p className="text-xs text-stone-500">
                          Provvigione {((pl.commission || 0) * 100).toFixed(1)}% · Ricarico {((pl.markup || 0) * 100).toFixed(1)}%
                        </p>
                      </div>
                      {sel && <Badge className="bg-[#8A1C2A] text-white">Selezionato</Badge>}
                    </button>
                  );
                })}
                {pricelists.length === 0 && (
                  <div className="px-4 py-8 text-center text-sm text-stone-500">Nessun listino disponibile</div>
                )}
              </div>
            </div>
          )}

          {/* STEP 3 — Items */}
          {step === 3 && (
            <div className="space-y-4" data-testid="do-step-3-pane">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Left: search products */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Aggiungi articoli dal listino</Label>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                    <Input
                      className="pl-10"
                      placeholder="Cerca per codice, descrizione, categoria..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      data-testid="do-product-search"
                    />
                  </div>
                  <div className="border border-stone-200 rounded-lg max-h-[55vh] overflow-y-auto divide-y divide-stone-100">
                    {productsLoading && <div className="px-4 py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}
                    {!productsLoading && filteredProducts.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => addProduct(p)}
                        className="w-full text-left px-3 py-2 flex items-center justify-between gap-2 hover:bg-stone-50"
                        data-testid={`do-add-product-${p.id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-stone-900 truncate">{displayDesc(p)}</p>
                          <p className="text-[11px] text-stone-500 font-mono">{p.code} · {p.category_name || "—"}</p>
                          {language === "en" && (p.description_en || "").trim() && (p.description_en !== p.description) && (
                            <p className="text-[10px] text-stone-400 italic truncate">IT: {p.description}</p>
                          )}
                          {language === "en" && !(p.description_en || "").trim() && (
                            <p className="text-[10px] text-amber-600 italic">⚠ Manca descrizione EN</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-[#8A1C2A]">€ {Number(p.price).toFixed(2)}</p>
                          <Plus className="w-4 h-4 inline text-emerald-600" />
                        </div>
                      </button>
                    ))}
                    {!productsLoading && filteredProducts.length === 0 && (
                      <div className="px-4 py-8 text-center text-sm text-stone-500">
                        {productSearch ? "Nessun prodotto" : "Tutti gli articoli disponibili sono già nell'offerta"}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: items in offer */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Articoli in offerta ({items.length})</Label>
                    <Button type="button" size="sm" variant="outline" onClick={() => setFreeOpen(true)} className="text-amber-700 border-amber-300 hover:bg-amber-50 h-7 text-xs" data-testid="do-add-free-btn">
                      <Plus className="w-3 h-3 mr-1" />Articolo libero
                    </Button>
                  </div>

                  {freeOpen && (
                    <div className="border-2 border-amber-300 bg-amber-50/40 rounded-lg p-3 space-y-2" data-testid="do-free-form">
                      <p className="text-xs font-semibold text-amber-800 flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> Nuovo articolo libero (non in catalogo)
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[10px] uppercase text-stone-500">Codice (opzionale)</Label>
                          <Input value={freeForm.code} onChange={(e) => setFreeForm(f => ({ ...f, code: e.target.value }))} className="h-8 text-sm" placeholder="LIB-001" data-testid="do-free-code" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase text-stone-500">U.M.</Label>
                          <Input value={freeForm.unit} onChange={(e) => setFreeForm(f => ({ ...f, unit: e.target.value }))} className="h-8 text-sm" placeholder="Kg" data-testid="do-free-unit" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase text-stone-500">Descrizione *</Label>
                        <Input value={freeForm.description} onChange={(e) => setFreeForm(f => ({ ...f, description: e.target.value }))} className="h-8 text-sm" placeholder="Es. Filetto di tonno fresco" data-testid="do-free-desc" />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase text-stone-500">Prezzo base € *</Label>
                        <Input type="number" min="0" step="0.01" value={freeForm.base_price} onChange={(e) => setFreeForm(f => ({ ...f, base_price: e.target.value }))} className="h-8 text-sm" placeholder="0.00" data-testid="do-free-price" />
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => { resetFreeForm(); setFreeOpen(false); }} data-testid="do-free-cancel">Annulla</Button>
                        <Button size="sm" onClick={addFreeItem} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="do-free-add">
                          <Plus className="w-3 h-3 mr-1" />Aggiungi
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="border border-stone-200 rounded-lg max-h-[50vh] overflow-y-auto divide-y divide-stone-100" data-testid="do-items-list">
                    {items.length === 0 && (
                      <div className="px-4 py-12 text-center text-sm text-stone-500">
                        Nessun articolo. Cerca a sinistra e aggiungi.
                      </div>
                    )}
                    {items.map((it, idx) => {
                      const itKey = it.product_id || `free-${idx}`;
                      return (
                      <div key={itKey} className={`px-3 py-2.5 space-y-1.5 ${it.is_free ? "bg-amber-50/30" : ""}`} data-testid={`do-item-${itKey}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <p className="text-sm font-medium text-stone-900 truncate">{displayDesc(it)}</p>
                              {it.is_free && <Badge variant="outline" className="text-[9px] py-0 px-1 border-amber-400 text-amber-700">LIBERO</Badge>}
                              {language === "en" && !it.is_free && !(it.description_en || "").trim() && (
                                <Badge variant="outline" className="text-[9px] py-0 px-1 border-amber-400 text-amber-700" title="Manca la descrizione EN nel prodotto — verrà usato l'italiano">EN?</Badge>
                              )}
                            </div>
                            <p className="text-[11px] text-stone-500 font-mono">{it.code || "—"} · {it.unit}{it.category_name ? <span className="text-[#C5A059]"> · {it.category_name}</span> : null}</p>
                          </div>
                          <div className="flex items-center gap-0.5 shrink-0">
                            <button onClick={() => moveItem(idx, -1)} disabled={idx === 0} className={`rounded p-1 ${idx === 0 ? "text-stone-300 cursor-default" : "text-stone-600 hover:bg-stone-100"}`} title="Sposta su" data-testid={`do-move-up-${itKey}`}>
                              <ChevronUp className="w-4 h-4" />
                            </button>
                            <button onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1} className={`rounded p-1 ${idx === items.length - 1 ? "text-stone-300 cursor-default" : "text-stone-600 hover:bg-stone-100"}`} title="Sposta giù" data-testid={`do-move-down-${itKey}`}>
                              <ChevronDown className="w-4 h-4" />
                            </button>
                            <button onClick={() => removeItem(idx)} className="text-rose-600 hover:bg-rose-50 rounded p-1" data-testid={`do-remove-item-${itKey}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 items-end">
                          <div>
                            <Label className="text-[10px] text-stone-500 uppercase">{it.is_free ? "Prezzo base" : "Listino"}</Label>
                            {it.is_free ? (
                              <Input type="number" min="0" step="0.01" value={it.base_price} onChange={(e) => updateFreeBasePrice(idx, e.target.value)} className="h-8 text-sm" data-testid={`do-free-base-${itKey}`} />
                            ) : (
                              <div className="h-8 text-sm text-stone-700 px-2 py-1 bg-stone-50 rounded border border-stone-200">
                                € {Number(it.base_price).toFixed(2)}
                              </div>
                            )}
                          </div>
                          <div>
                            <Label className="text-[10px] text-stone-500 uppercase">Sconto %</Label>
                            <Input
                              type="number"
                              min="0"
                              max="99"
                              step="0.5"
                              value={it.discount_pct}
                              onChange={(e) => updateDiscount(idx, e.target.value)}
                              className="h-8 text-sm"
                              data-testid={`do-discount-${itKey}`}
                            />
                          </div>
                          <div>
                            <Label className="text-[10px] text-stone-500 uppercase">Dedicato</Label>
                            <div className="h-8 text-sm font-bold text-[#8A1C2A] px-2 py-1 bg-amber-50 rounded border border-amber-200 text-right" data-testid={`do-final-price-${itKey}`}>
                              € {computedFinal(it).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-1">
                  <Label className="text-sm font-medium">Note (opzionale, visibili nel PDF)</Label>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-stone-500 whitespace-nowrap">Lingua PDF:</Label>
                    <Select value={language} onValueChange={setLanguage}>
                      <SelectTrigger className="h-8 w-[140px]" data-testid="do-language-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="it">Italiano</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Es. Validità accordata, modalità di consegna concordate..."
                  className="text-sm"
                  rows={2}
                  data-testid="do-notes"
                />
              </div>
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="flex-row items-center justify-between gap-2 pt-3 border-t border-stone-100">
          <div className="text-xs text-stone-500">
            {isProspect && prospectData.business_name && (
              <>Prospect: <span className="font-medium text-amber-700">{prospectData.business_name}</span></>
            )}
            {!isProspect && customerId && (
              <>Cliente: <span className="font-medium text-stone-700">{customers.find(c => c.id === customerId)?.business_name || "—"}</span></>
            )}
            {pricelistId && (
              <>{(isProspect ? prospectData.business_name : customerId) && " · "}Listino: <span className="font-medium text-stone-700">{pricelists.find(p => p.id === pricelistId)?.name || "—"}</span></>
            )}
          </div>
          <div className="flex gap-2">
            {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="do-back-btn"><X className="w-4 h-4 mr-1" />Indietro</Button>}
            {step < 3 && (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={(step === 1 && !canProceedStep1) || (step === 2 && !canProceedStep2)}
                className="bg-[#8A1C2A] hover:bg-[#6e1622]"
                data-testid="do-next-btn"
              >
                Avanti →
              </Button>
            )}
            {step === 3 && (
              <>
                <Button variant="outline" onClick={() => handleSave(true)} disabled={saving} data-testid="do-save-pdf-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
                  Salva e apri PDF
                </Button>
                <Button onClick={() => handleSave(false)} disabled={saving} className="bg-[#8A1C2A] hover:bg-[#6e1622]" data-testid="do-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Salva
                </Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
